import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { array, number, object, string, boolean, mixed } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { IStationTwoForm } from 'interfaces/StationForms.ts';
import { GenderInfo } from '@/common/enum';

export const defaultValues: IStationTwoForm = {
  id: 0,
  activeIdx: null,
  driverLicense: '',
  firstName: '',
  lastName: '',
  dob: null,
  ssn: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  gender: GenderInfo.Male,
  contaminationStatus: null,
  wristBand: null,
  wristBandCode: null,
  model52: {
    L3: false,
    R3: false,
    L2: false,
    R2: false,
    L1: false,
    R1: false,
    Head: false,
    Feet: false,
    operator: '',
    scribe: '',
    scannerSerial: '',
  },
  model521: {
    LT: false,
    RT: false,
    LB: false,
    RB: false,
    operator: '',
    scribe: '',
    scannerSerial: '',
  },
  meterScreening: {
    hair: 0,
    head: 0,
    neck: 0,
    shoulderL: 0,
    shoulderR: 0,
    armL: 0,
    armR: 0,
    handL: 0,
    handR: 0,
    chest: 0,
    back: 0,
    legL: 0,
    legR: 0,
    footL: 0,
    footR: 0,
    operator: '',
    scribe: '',
    scannerNo: '',
    probeSerial: '',
  },
  screenings: [],
  members: [],
  postInfo: {
    index: 0,
    post: '',
  },
};

const isAdult = (dob: Date | string | null | undefined): boolean => {
  if (!dob) return false;
  const today = new Date();
  const birthDate = new Date(dob as string);
  if (isNaN(birthDate.getTime())) return false;
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 18;
};

function useStationTwoForm() {
  const validationSchema = useMemo(
    () =>
      object().shape({
        memberId: number().required('Member ID is required').min(1, 'Not a valid member ID'),
        stationState: number(),
        // A status is still required, but with a readable message — the bare string()
        // rule reported yup's raw default ("contaminationStatus cannot be null").
        contaminationStatus: string().nullable().required('Please select a contamination status.'),
        // The band is applied via the APPLY WRISTBAND modal and is null until then;
        // allow null so that modal never shows "wristBand cannot be null". A status
        // selection always assigns the band, so this cannot be null on a valid submit.
        wristBand: string().nullable(),
        wristBandCode: string().nullable(),
        email: mixed().when('dob', {
          is: (dob: Date | string | null) => isAdult(dob),
          then: () => string().email('Must be a valid email').required('Email required for adults (18+)'),
          otherwise: () => string().email('Must be a valid email').nullable(),
        }),
        phone: mixed().when('dob', {
          is: (dob: Date | string | null) => isAdult(dob),
          then: () => string().required('Phone number required for adults (18+)'),
          otherwise: () => string().nullable(),
        }),
        model52: object().shape({
          L3: boolean(),
          R3: boolean(),
          L2: boolean(),
          R2: boolean(),
          L1: boolean(),
          R1: boolean(),
          Head: boolean(),
          Feet: boolean(),
        }),
        model521: object().shape({
          LT: boolean(),
          RT: boolean(),
          LB: boolean(),
          RB: boolean(),
        }),
        screenings: array()
          .of(
            object().shape({
              screeningType: number(),
            })
          )
          .min(1, 'Must have at least one screening'),
      }),
    []
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useForm<any>({ defaultValues, resolver: yupResolver(validationSchema) });
}

export default useStationTwoForm;
